import { useMemo } from 'react';

/**
 * Custom hook to prepare data for StartDesignModal
 */
export function useModalData(selectedPlan, selectedLocation, locations, elevations, rooms) {
    return useMemo(() => {
        if (!selectedPlan) return { communities: [], elevations: [], scenes: [] };

        const planName = selectedPlan.planName;
        
        console.log('Getting data for plan:', planName, 'location:', selectedLocation);
        
        // Get communities for this floorplan and location
        const planCommunities = locations
            .filter(loc => {
                const locFloorplan = loc.Floorplans || loc.floorplans;
                const locLocation = loc.Location || loc.location;
                return locFloorplan === planName && locLocation === selectedLocation;
            })
            .map(loc => loc.Community || loc.community)
            .filter(Boolean);
        const uniqueCommunities = [...new Set(planCommunities)];
        
        console.log('Communities:', uniqueCommunities);

        // Get elevations for this floorplan
        const planElevations = elevations
            .filter(elev => (elev.Floorplan || elev.floorplan) === planName)
            .map((elev) => {
                const elevId = elev.Id || elev.id;
                const elevName = elev.Elevation || elev.elevation;
                
                // Find exterior room for this elevation
                const exteriorRoom = rooms.find(room => {
                    const roomElevId = room.ElevationId || room.elevationId;
                    const roomArea = room.Area || room.area;
                    return roomElevId === elevId && roomArea === 'Exterior';
                });
                
                // Generate thumbnail URL
                let thumbnailUrl = '';
                if (exteriorRoom) {
                    const spec = exteriorRoom.Specifier || exteriorRoom.specifier || '';
                    const sel = exteriorRoom.Sel || exteriorRoom.sel || '-1';
                    
                    if (spec) {
                        // Normalize specifier path (replace backslashes with forward slashes)
                        const normalizedSpec = spec.replace(/\\/g, '/');
                        // Use sel if valid, otherwise use -1
                        const normalizedSel = sel && sel !== '' ? sel : '-1';
                        thumbnailUrl = `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(normalizedSpec)}&sel=${encodeURIComponent(normalizedSel)}&type=base&size=500`;
                    }
                }
                
                return {
                    id: elevId,
                    name: elevName || `Elevation ${elevId}`,
                    image: thumbnailUrl || `/images/elevations/${planName.toLowerCase()}-${(elevName || '').toLowerCase().replace(/\s+/g, '-')}.png`,
                    specifier: exteriorRoom?.Specifier || exteriorRoom?.specifier,
                    sel: String(exteriorRoom?.Sel || exteriorRoom?.sel || '-1'),
                    originalData: elev
                };
            })
            .filter(elev => elev.id);
        
        console.log('Elevations:', planElevations);

        // Get all scenes (rooms) for the elevations
        const elevationIds = planElevations.map(e => e.id);
        const planScenes = rooms
            .filter(room => {
                const roomElevId = room.ElevationId || room.elevationId;
                return elevationIds.includes(roomElevId);
            })
            .map((room) => {
                const roomArea = room.Area || room.area;
                const spec = room.Specifier || room.specifier || '';
                // Ensure sel is always a string (Excel might store it as a number)
                const rawSel = room.Sel || room.sel || '-1';
                const sel = String(rawSel);
                
                let thumbnailUrl = '';
                if (spec) {
                    // Normalize specifier path (replace backslashes with forward slashes)
                    const normalizedSpec = spec.replace(/\\/g, '/');
                    // Use sel if valid, otherwise use -1
                    const normalizedSel = sel && sel !== '' ? sel : '-1';
                    thumbnailUrl = `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(normalizedSpec)}&sel=${encodeURIComponent(normalizedSel)}&type=base&size=500`;
                }
                
                return {
                    id: room.ElevationId || room.elevationId,
                    elevationId: room.ElevationId || room.elevationId,
                    name: roomArea || 'Room',
                    area: roomArea,
                    image: thumbnailUrl || `/images/scenes/${planName.toLowerCase()}-${(roomArea || '').toLowerCase().replace(/\s+/g, '-')}.png`,
                    specifier: spec,
                    sel: sel,
                    originalData: room
                };
            })
            .filter(scene => scene.area);
        
        console.log('Scenes:', planScenes);

        return {
            communities: uniqueCommunities,
            elevations: planElevations,
            scenes: planScenes
        };
    }, [selectedPlan, selectedLocation, locations, elevations, rooms]);
}
